import {
  Text as RNText,
  type StyleProp,
  type TextProps as RNTextProps,
  type TextStyle,
} from 'react-native';

import { parentType, textStyleFor, type ParentVariant } from '../theme/typography';
import { colors } from '../tokens';

export type TextTone =
  | 'ink'
  | 'slate'
  | 'muted'
  | 'onTurquoise'
  | 'onGradient'
  | 'link'
  | 'magenta'
  | 'orange'
  | 'error'
  | 'white';

export const toneColor: Record<TextTone, string> = {
  ink: colors.ink,
  slate: colors.slate,
  muted: colors.muted,
  onTurquoise: colors.white,
  onGradient: colors.white,
  link: colors.deepTeal,
  magenta: colors.magenta,
  orange: colors.orange,
  error: colors.error,
  white: colors.white,
};

/**
 * Font family, weight, size and line-height are reachable only through `variant`. The keys are typed `never`
 * (not merely omitted) so a StyleSheet.create object carrying them is rejected too — Omit alone lets non-literal
 * objects through because TypeScript skips excess-property checks on them.
 */
interface ForbiddenTextStyle {
  fontFamily?: never;
  fontWeight?: never;
  fontSize?: never;
  lineHeight?: never;
  fontStyle?: never;
}
export type SafeTextStyle = Omit<TextStyle, keyof ForbiddenTextStyle> & ForbiddenTextStyle;

export interface TextProps extends Omit<RNTextProps, 'style'> {
  variant?: ParentVariant;
  tone?: TextTone;
  style?: StyleProp<SafeTextStyle>;
}

/** The only text primitive. Poppins only; sizes and weights from the parent scale. */
export function Text({ variant = 'body', tone = 'ink', style, ...rest }: TextProps) {
  return (
    <RNText
      {...rest}
      style={[textStyleFor(parentType[variant]), { color: toneColor[tone] }, style]}
    />
  );
}

type VariantProps = Omit<TextProps, 'variant'>;

export const Title = (p: VariantProps) => <Text variant="title" {...p} />;
export const Title2 = (p: VariantProps) => <Text variant="title2" {...p} />;
export const CardTitle = (p: VariantProps) => <Text variant="cardTitle" {...p} />;
export const SectionTitle = (p: VariantProps) => <Text variant="sectionTitle" {...p} />;
export const Body = (p: VariantProps) => <Text variant="body" {...p} />;
export const BodySm = (p: VariantProps) => <Text variant="bodySm" {...p} />;
export const HelperText = (p: VariantProps) => <Text variant="helper" {...p} />;
export const Label = (p: VariantProps) => <Text variant="label" {...p} />;
export const Meta = (p: VariantProps) => <Text variant="meta" tone="muted" {...p} />;
export const Eyebrow = (p: VariantProps) => <Text variant="eyebrow" {...p} />;
export const Overline = (p: VariantProps) => <Text variant="overline" {...p} />;
export const RowTitle = (p: VariantProps) => <Text variant="rowTitle" {...p} />;
export const TextLink = (p: VariantProps) => <Text variant="textLink" tone="link" {...p} />;
export const ButtonLabel = (p: VariantProps) => <Text variant="button" {...p} />;
